import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, ChevronDown } from "lucide-react";

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen hero-gradient flex items-center pt-20"
      data-testid="hero-section"
    >
      <div></div>
      <div className="container-custom">
        <div className="flex justify-center items-center py-5">
          <h1 className="text-4xl md:text-4xl font-bold bg-[#0066CC] bg-clip-text text-transparent text-center">
            TRICUBE INNOSOFT TECHNOLOGIES LLP
          </h1>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-[#0066CC]/10 text-[#0066CC] text-sm font-semibold px-4 py-2 rounded-full mb-6"
            >
              <span className="w-2 h-2 bg-[#0066CC] rounded-full animate-pulse" />
              MyCafé Connect by TISTL
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#F5A623] mb-6"
            >
              Re-imagine Your{" "}
              <span className="text-[#0066CC]">Corporate Dining</span>{" "}
              <span className="text-[#FFC000]">Experience</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-xl"
            >
              Seamless, cashless, contactless and smart cafeteria solutions for the modern
              workplace. Empower your employees with digital food ordering,
              secure payments, and real-time analytics.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-wrap items-center gap-4"
            >
              <Button
                onClick={() => scrollToSection("#contact")}
                className="btn-primary flex items-center gap-2 text-base"
                data-testid="hero-get-started-btn"
              >
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Button>

              <Button
                variant="outline"
                onClick={() => scrollToSection("#services")}
                className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-gray-200 hover:border-[#0066CC] hover:text-[#0066CC] transition-all duration-300 text-base"
                data-testid="hero-services-btn"
              >
                <Play className="w-5 h-5" />
                Our Services
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex items-center gap-8 mt-12 pt-8 border-t border-gray-200"
            >
              <div>
                <div className="stat-number">20+</div>
                <div className="text-sm text-gray-600 mt-1">Cafeterias</div>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div>
                <div className="stat-number">2.5L</div>
                <div className="text-sm text-gray-600 mt-1">Monthly Orders</div>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div>
                <div className="stat-number">99.9%</div>
                <div className="text-sm text-gray-600 mt-1">Uptime</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://s3-cc-prod.s3.ap-south-1.amazonaws.com/tistl/Website+first+cafe+image.png"
                alt="Modern corporate cafeteria"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>

            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="absolute -left-4 lg:-left-8 bottom-8 bg-white rounded-2xl shadow-xl p-4 hidden md:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Cashless</div>
                  <div className="text-sm text-gray-500">
                    100% Digital Payments
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Floating Card 2 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="absolute -right-4 lg:-right-8 top-8 bg-white rounded-2xl shadow-xl p-4 hidden md:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#F5A623]/20 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-[#F5A623]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Fast Orders</div>
                  <div className="text-sm text-gray-500">Skip the Queue</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <button
          onClick={() => scrollToSection("#about")}
          className="flex flex-col items-center gap-2 text-gray-400 hover:text-[#0066CC] transition-colors"
          data-testid="scroll-indicator-btn"
        >
          <span className="text-xs font-medium uppercase tracking-wider">
            Scroll Down
          </span>
          <ChevronDown className="w-5 h-5 scroll-indicator" />
        </button>
      </motion.div>
    </section>
  );
};

export default Hero;
